import React, { useState, useEffect } from 'react';
import { Toast } from '../../../components/common/Toast';
import MainPageLayout from '../../../components/common/MainPageLayout';
import FormCard from '../../../components/common/FormCard';
import TextEditor from './Texteditor';
import { SaveButtonIcon } from '../../../components/common/SvgIcon';
import AllShortCodes from '../../AddNewPage/components/AllShortCodes';

const EditTemplatePage = ({ templateId }) => {
  const [template, setTemplate] = useState(null);
  const [content, setContent] = useState('');
  const [shortcodes, setShortcodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplate();
    fetchShortcodes();
  }, [templateId]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/templates/${templateId}`, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTemplate(result.data);
        setContent(result.data.content || '');
      } else {
        Toast.error('Failed to load template');
        window.location.hash = '/legal-page-templates';
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      Toast.error('An error occurred while loading template');
      window.location.hash = '/legal-page-templates';
    } finally {
      setLoading(false);
    }
  };

  const fetchShortcodes = async () => {
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/shortcodes`, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();
      if (result.success) {
        setShortcodes(result.data || {});
      }
    } catch (error) {
      console.error('Error fetching shortcodes:', error);
    }
  };

  const handleEditorChange = (newContent) => {
    setContent(newContent);
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      Toast.success('Shortcode copied!');
    } catch (err) {
      Toast.error('Failed to copy');
    }
    document.body.removeChild(textArea);
  };

  const copyShortcode = (shortcode) => {
    const placeholder = `[${shortcode}]`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(placeholder)
        .then(() => Toast.success('Shortcode copied!'))
        .catch(() => fallbackCopy(placeholder));
    } else {
      fallbackCopy(placeholder);
    }
  };

  const handleMediaUpload = () => {
    if (typeof wp === 'undefined' || typeof wp.media === 'undefined') {
      console.error('WordPress media library is not available');
      Toast.error('Media library is not available');
      return;
    }

    const frame = wp.media({
      title: 'Select Image',
      button: { text: 'Use selected image' },
      multiple: false,
    });

    frame.on('select', () => {
      const attachment = frame.state().get('selection').first().toJSON();
      const imgHtml = `<img src="${attachment.url}" alt="${attachment.alt || attachment.title}" style="max-width:100%;">`;

      const editor = window.tinymce.get('legal-pages-template-editor');
      if (editor) editor.insertContent(imgHtml);
    });

    frame.open();
  };

  const handleUpdate = async () => {
    if (!content.trim()) {
      Toast.error('Template content cannot be empty');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/template/${templateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify({
          content,
        }),
      });

      const result = await response.json();

      if (result.success) {
        Toast.success('Template updated successfully!');
        setTimeout(() => {
          window.location.hash = '/legal-page-templates';
        }, 1500);
      } else {
        Toast.error('Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      Toast.error('An error occurred while updating');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    window.location.hash = '/legal-page-templates';
  };

  if (loading) {
    return (
      <MainPageLayout
        title="Edit Template"
        description="Loading template..."
      >
        <div className="space-y-3 mt-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="h-14 bg-gray-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      </MainPageLayout>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <MainPageLayout
      title="Edit Template"
      description={`Modify the ${template.name} template`}
    >
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-2 px-4 py-2 text-[#6F6F7B] hover:text-[#17171C] transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Templates
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Template Name + Editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Template Name Card - Styled like input field */}
          <FormCard className="!py-10 !px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-[#17171C]">
                  {template.name}
                </h2>
                <span className={`
                  inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                  ${template.type === 'free'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-purple-50 text-purple-700 border border-purple-200'
                  }
                `}>
                  {template.type === 'pro' && '⭐'}
                  {template.type === 'free' ? 'FREE' : 'PRO'}
                </span>
              </div>
            </div>
          </FormCard>

          {/* Text Editor Card */}
          <FormCard className="mt-6 overflow-hidden">
            <div className="p-2">
              <TextEditor
                content={content}
                onChange={handleEditorChange}
                handleMediaUpload={handleMediaUpload}
                editorId="legal-pages-template-editor"
              />
            </div>
          </FormCard>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleUpdate}
              disabled={saving}
              className={`
                inline-flex items-center gap-2 px-8 py-[14px] cursor-pointer rounded-xl font-semibold text-white transition shadow-[0px_4px_14px_-2px_#7C3BED40]
                ${saving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-lgp-color'}
              `}
            >
              <SaveButtonIcon />
              {saving ? 'Updating...' : 'Update Template'}
            </button>

            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-[14px] cursor-pointer border border-[#E4E4E7] text-[#6F6F7B] rounded-xl font-semibold hover:border-[#17171C] hover:text-[#17171C] transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Column - Shortcodes */}
        <div className="lg:col-span-1">
          <FormCard>
            <AllShortCodes
              shortcodes={shortcodes}
              copyShortcode={copyShortcode}
              loading={loading}
            />
          </FormCard>
        </div>
      </div>
    </MainPageLayout>
  );
};

export default EditTemplatePage;